import { MutationUpdaterFn } from "@apollo/client/core/types";
import { TrashIcon } from "@heroicons/react/outline";
import firebase from "firebase/app";
import { useTranslation } from "next-i18next";
import React, { useState } from "react";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { CommentActionButton } from "@/features/comment/components/CommentCore/CommentActionButton";
import { StandardConfirmDialogView } from "@/features/common/components/layout/StandardConfirmDialogView";
import { StandardDialog } from "@/features/common/components/layout/StandardDialog";
import { useSnackbar } from "@/features/common/hooks/snackbar/useSnackbar";
import { useHandleGraphqlError } from "@/features/common/hooks/useHandleGraphqlError";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  useRemoveCommentMutation,
  RemoveCommentMutation,
} from "@/generated/generated-hooks";

export const CommentRemoveButton: React.FC<{
  commentId: string;
  isPostOwner: boolean;
}> = ({ commentId, isPostOwner }) => {
  const { t } = useTranslation("post");
  const { user } = useUserContext();
  const [removeComment, { loading }] = useRemoveCommentMutation({
    update: updateAfterRemoveComment,
  });
  const handleGraphqlError = useHandleGraphqlError();
  const { openSnackbar } = useSnackbar();

  const [openDialog, setOpenDialog] = useState(false);
  const onClickRemoveButton = () => {
    setOpenDialog(true);
    firebase
      .analytics()
      .logEvent(AnalyticsEventEnum.CommentRemoveClick, { commentId });
  };

  const onConfirm = async () => {
    firebase
      .analytics()
      .logEvent(AnalyticsEventEnum.CommentRemoveDialogConfirm, { commentId });

    try {
      await removeComment({ variables: { commentId } });
      openSnackbar({
        message: t("post:comment.successfullyRemoved"),
        severity: "success",
      });
    } catch (e) {
      handleGraphqlError({
        e,
        t,
        sentryCaptureContext: {
          tags: {
            mutations: "useRemoveCommentMutation",
          },
        },
      });
    }
  };

  const onCancel = () => {
    setOpenDialog(false);
    firebase
      .analytics()
      .logEvent(AnalyticsEventEnum.CommentRemoveDialogCancel, { commentId });
  };

  if (!user || !isPostOwner) return null;

  return (
    <>
      <StandardDialog open={openDialog}>
        <StandardConfirmDialogView
          loading={loading}
          title={t("post:comment.removeCommentDialogTitle")}
          message={t("post:comment.removeCommentDialogMessage")}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      </StandardDialog>
      <CommentActionButton
        onClick={onClickRemoveButton}
        icon={TrashIcon}
        label={t("post:comment.removeComment")}
      />
    </>
  );
};

const updateAfterRemoveComment: MutationUpdaterFn<RemoveCommentMutation> = (
  cache,
  { data: { removeComment } },
) => {
  /* update number of replies */
  const isReplyingToComment = !!removeComment.parentCommentId;

  const updateReplyingToComment = () => {
    cache.modify({
      id: cache.identify({
        __typename: "Comment",
        _id: removeComment.parentCommentId,
      }),
      fields: {
        numberOfReplies(existingNumberOfReplies) {
          return existingNumberOfReplies - 1;
        },
      },
    });
  };

  const updateCommentingToPost = () => {
    cache.modify({
      id: cache.identify({ __typename: "Post", _id: removeComment.postId }),
      fields: {
        commentCount(existingCommentCount) {
          return existingCommentCount - 1;
        },
      },
    });
  };

  if (isReplyingToComment) {
    updateReplyingToComment();
  } else {
    updateCommentingToPost();
  }
};
